
import { mapState } from 'vuex'
import { remapI18nAlternates } from '@/utils/hreflang'
import { htmlLang } from '@/utils/locales'
import { errorHeadMeta, errorTitleKey } from '@/utils/errorHead'
import TheHeader from '@/components/Header'
import TheFooter from '@/components/Footer'
import ScrollTop from '@/components/ScrollTop'
import TheMenu from '@/components/Menu'
import HeaderPromotionalBanner from '@/components/HeaderPromotionalBanner.vue'
import ClockChangeBanner from '@/components/ClockChangeBanner.vue'
import CommercialOperationBanner from '@/components/CommercialOperationBanner.vue'
import { topBannerKind } from '@/utils/welcomeOffer'
import { halloweenFor } from '@/utils/halloween'
import { clockChangeBannerFor } from '@/utils/clockChange'

export default {
  name: 'Default',
  components: {
    ScrollTop,
    TheFooter,
    TheHeader,
    SearchOverlay: () => import('@/components/SearchOverlay'),
    TheMenu,
    LedSearchForm: () => import('@/components/LedSearchForm'),
    HeaderPromotionalBanner,
    ClockChangeBanner,
    CommercialOperationBanner,
    ModalProductAddedToCart: () =>
      import('@/components/ModalProductAddedToCart'),
    ModalAddAlertUnknownRegistration: () =>
      import('@/components/ModalAddAlertUknownRegistration'),
    MobileMenu: () => import('@/components/MobileMenu'),
    ModalSubscriptionNewsletter: () =>
      import('@/components/ModalSubscriptionNewsletter'),
    CountriesLanguagesSidebar: () =>
      import('@/components/CountriesLanguagesSidebar'),
    VehiclesSelectorSidebar: () =>
      import('@/components/VehiclesSelectorSidebar'),
    ProductsSidebar: () => import('@/components/ProductsSidebar'),
  },
  data() {
    return {
      mobileMenu: false,
      showOverlay: false,
      easterModalVisible: false,
      easterModalLedData: { id: 'red', color: '#ef4444', colorName: 'easterHunt.colorRed', image: '/gifs/led-rouge.png' },
      easterModalFoundCount: 0,
      easterModalFoundLeds: [],
      sendingVerificationEmail: false,
    }
  },
  head() {
    const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })
    const htmlAttrs = { ...i18nHead.htmlAttrs }
    const lang = htmlLang(this.$i18n.localeProperties)
    if (lang) htmlAttrs.lang = lang
    const isErrorState = Boolean(this.$root?.$options?.nuxt?.err)

    if (isErrorState) {
      const statusCode = this.$root?.$options?.nuxt?.err?.statusCode
      return {
        htmlAttrs,
        titleTemplate: '%s',
        title: this.$t(errorTitleKey(statusCode)),
        meta: [
          { name: 'theme-color', content: '#14181e' },
          ...errorHeadMeta(key => this.$t(key)),
        ],
        link: [],
      }
    }

    const isRegionalHreflang = code =>
      typeof code === 'string' && code !== 'x-default' && code.includes('-')
    const restrictedLocales = this.$store.state.restricted_locales || []
    const restrictedHids = new Set(
      restrictedLocales.flatMap(code => {
        const locale = this.$i18n.locales.find(l => l.code === code)
        return locale
          ? [`i18n-alt-${locale.iso}`, `i18n-alt-${code}`]
          : [`i18n-alt-${code}`]
      })
    )
    if (restrictedLocales.includes(this.$i18n.defaultLocale)) {
      restrictedHids.add('i18n-xd')
    }

    return {
      htmlAttrs,
      titleTemplate: this.$route.path != '/' ? '%s' : '',
      meta: [{ name: 'theme-color', content: '#14181e' }],
      link: remapI18nAlternates(i18nHead.link, this.$i18n.locales)
        .filter(
          l =>
            l.rel !== 'canonical' &&
            !restrictedHids.has(l.hid) &&
            !(l.rel === 'alternate' && isRegionalHreflang(l.hreflang))
        )
        .map(l => {
          if (l.rel === 'alternate' && typeof l.href === 'string') {
            const queryIndex = l.href.indexOf('?')
            if (queryIndex !== -1) {
              return { ...l, href: l.href.slice(0, queryIndex) }
            }
          }
          return l
        }),
    }
  },
  computed: {
    ...mapState(['cart']),
    welcomeOffer() {
      return this.$store.getters.welcome_offer_for_visitor
    },
    topBanner() {
      return topBannerKind({
        clockChange: clockChangeBannerFor(this.$store.state, this.$i18n.locale),
        operationBannerPhase: this.$store.getters.operation_banner_phase,
        operationSlug: this.$store.state.commercial_operation?.slug,
        manualBannerVisible: this.$store.getters.manual_banner_visible,
        welcomeOffer: this.welcomeOffer,
        routeName: this.$route.name,
      })
    },
    showTopBanner() {
      return this.topBanner !== null
    },
    halloween() {
      return halloweenFor(this.$store.state, this.$route.name)
    },
    welcomeDiscount() {
      return this.$t(
        'components.headerPromotionalBanner.welcomeOfferDiscount',
        {
          value: this.welcomeOffer?.value,
        }
      )
    },
    topBannerText() {
      if (this.topBanner === 'welcome') {
        return this.$t('components.headerPromotionalBanner.welcomeOffer', {
          discount: this.welcomeDiscount,
          code: this.welcomeOffer.code,
        })
      }
      return this.$store.state.banner || ''
    },
    topBannerHref() {
      if (this.topBanner === 'welcome') {
        return this.localePath({ name: 'code-promo' })
      }
      return this.$store.state.banner_url || ''
    },
    isHomePage() {
      // Détecte si on est sur la page d'accueil (index ou index avec locale)
      return (
        this.$route.name === 'index' ||
        this.$route.name?.startsWith('index__') ||
        this.$route.path === '/'
      )
    },
  },
  created() {
    this.$store.dispatch('loadVehicleSearchHistory').then(() => {
      this.$store.dispatch('loadSelectedVehicle')
    })
  },
  mounted() {
    this.refreshClock()
    if (typeof this.$nuxt !== 'undefined') {
      this.$nuxt.$on('led-found', this.onEasterLedFound)
    }
  },
  beforeDestroy() {
    if (typeof this.$nuxt !== 'undefined') {
      this.$nuxt.$off('led-found', this.onEasterLedFound)
    }
  },
  watch: {
    $route(to, from) {
      const shouldRedirect =
        this.isSameProductRoute(from, to) &&
        this.hasSpecificQueryConditions(from, to)

      if (shouldRedirect) {
        this.$router.go(-1)
        return
      }
      this.resetUIState()
      this.refreshClock()
      this.$store.commit('SET_ALTERNATE_URLS', {})
      this.$store.commit('SET_RESTRICTED_LOCALES', [])
    },
  },

  methods: {
    refreshClock() {
      this.$store.commit(
        'SET_CLOCK_NOW',
        Date.now() + (this.$store.state.clock_offset || 0)
      )
    },
    isSameProductRoute(from, to) {
      return from.path === to.path
    },

    hasSpecificQueryConditions(from, to) {
      return !to.query.q && Boolean(from.query.variant) && Boolean(to.query.fv)
    },

    resetUIState() {
      this.mobileMenu = false
      this.showOverlay = false
      this.$bvModal.hide('modalProductAddedToCart')
    },

    handleCloseModalDefault() {
      this.$bvModal.hide('modalSearchLed')
      this.mobileMenu = !this.mobileMenu
    },
    async logoutAdmin() {
      await this.$auth.logout()
      this.$nuxtCookies.remove('admin_logged_in_user', { path: '/' })
      if (process.client) window.location.reload()
    },

    sendVerificationEmail() {
      if (this.sendingVerificationEmail) return

      this.sendingVerificationEmail = true
      this.$userService
        .sendVerificationEmail()
        .then(() => {
          this.$swalCustomSuccess(
            this.$t('layouts.default.verificationEmailSentTitle'),
            this.$t('layouts.default.verificationEmailSentMessage')
          )
        })
        .catch(error => {
          this.$swalErrorMessage(error)
        })
        .finally(() => {
          this.sendingVerificationEmail = false
        })
    },
    collapseMobileMenu(value) {
      if (value === false) {
        this.mobileMenu = value
      } else {
        this.mobileMenu = !this.mobileMenu
      }
    },
    onEasterLedFound(data) {
      const led = { id: data.ledId, color: data.color, colorName: data.colorName, image: data.image }
      this.easterModalLedData = led
      this.easterModalFoundCount = data.foundCount
      if (process.client) {
        try {
          const stored = localStorage.getItem('easter_hunt_2026')
          this.easterModalFoundLeds = stored ? JSON.parse(stored) : []
        } catch {
          this.easterModalFoundLeds = []
        }
      }
      this.easterModalVisible = true
    },
    async subscribeEasterNewsletter() {
      if (!this.$auth.user?.newsletter_subscription) return
      const subId = this.$auth.user.newsletter_subscription.id
      const status = this.$auth.user.newsletter_subscription.newsletter_subscription_status?.identifier
      if (status === 'disabled') {
        await this.$newsletterSubService.enable(subId)
        this.$auth.fetchUser()
      }
    },
  },
}
